import { useState, useEffect, useCallback } from 'react';
import { createApi } from '../services/api.js';

type Tier = 'free' | 'fortress' | 'citadel';

const TIER_ORDER: Tier[] = ['free', 'fortress', 'citadel'];

const TIERS: Array<{
  id: Tier;
  name: string;
  desc: string;
  price: string;
  period?: string;
  features: string[];
}> = [
  {
    id: 'free',
    name: 'Shield',
    desc: 'Free forever',
    price: '€0',
    features: ['5 analyses/day', 'Score 0–100', '8 categories', 'Plain translation'],
  },
  {
    id: 'fortress',
    name: 'Fortress',
    desc: 'For heavy email users',
    price: '€9',
    period: '/mo',
    features: ['Unlimited analyses', 'Diplomatic reply drafts', 'Sender profiling', 'Weekly report'],
  },
  {
    id: 'citadel',
    name: 'Citadel',
    desc: 'Maximum protection',
    price: '€19',
    period: '/mo',
    features: ['Everything in Fortress', 'Auto-send replies', 'Evidence Vault (AES-256)', 'Priority support'],
  },
];

interface SubInfo {
  status: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  scheduled_change: string | null;
}

export function PricingPage({ token }: { token: string }) {
  const [loading, setLoading] = useState<string | null>(null);
  const [currentTier, setCurrentTier] = useState<Tier | null>(null);
  const [subInfo, setSubInfo] = useState<SubInfo | null>(null);
  const [fetching, setFetching] = useState(true);
  const [copied, setCopied] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const api = createApi(token);

  useEffect(() => {
    Promise.all([api.getUsage(), api.getSubscription()]).then(([usageRes, subRes]) => {
      if (usageRes.ok) {
        setCurrentTier(usageRes.data.tier as Tier);
      } else {
        setCurrentTier('free');
      }
      if (subRes.ok && subRes.data.subscription) {
        setSubInfo(subRes.data.subscription);
      }
      setFetching(false);
    });
  }, [token]);

  const handleUpgrade = useCallback(async (tier: 'fortress' | 'citadel') => {
    setLoading(tier);
    const result = await api.createCheckout(tier);
    if (result.ok) {
      window.location.href = result.data.checkout_url;
    }
    setLoading(null);
  }, [api]);

  const handleManage = useCallback(async () => {
    setPortalLoading(true);
    const result = await api.getBillingPortal();
    if (result.ok) {
      window.location.href = result.data.portal_url;
    }
    setPortalLoading(false);
  }, [api]);

  const referralLink = `https://aasshield.com/?ref=${btoa(token.slice(-12)).replace(/=/g, '')}`;

  const handleCopyReferral = useCallback(() => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [referralLink]);

  if (fetching) {
    return (
      <div>
        <h1 className="text-xl font-bold text-ink mb-6">Pricing</h1>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-surface-200 bg-white p-6 animate-pulse">
              <div className="h-4 w-16 bg-surface-100 rounded mb-2" />
              <div className="h-6 w-24 bg-surface-100 rounded mb-4" />
              <div className="h-10 w-20 bg-surface-100 rounded mb-4" />
              <div className="space-y-2">
                {[0, 1, 2, 3].map((j) => (
                  <div key={j} className="h-4 w-full bg-surface-100 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const currentIdx = TIER_ORDER.indexOf(currentTier ?? 'free');
  const isPaid = currentTier !== 'free' && currentTier !== null;

  return (
    <div>
      <h1 className="text-xl font-bold text-ink mb-6">Pricing</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {TIERS.map((tier, i) => {
          const isCurrent = tier.id === currentTier;
          const isUpgrade = i > currentIdx;
          const isDowngrade = i < currentIdx;
          const highlight = tier.id === 'fortress';

          return (
            <div
              key={tier.id}
              className={`relative rounded-xl border p-6 ${
                isCurrent
                  ? 'border-brand bg-red-50/40 ring-1 ring-brand/20'
                  : highlight
                    ? 'border-brand/40 bg-red-50/20'
                    : 'border-surface-200 bg-white'
              }`}
            >
              {isCurrent && (
                <div className="absolute -top-2.5 left-4">
                  <span className="rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    Current plan
                  </span>
                </div>
              )}

              <p className="text-xs text-ink-muted">{tier.desc}</p>
              <h3 className="mt-1 text-lg font-bold text-ink">{tier.name}</h3>
              <div className="mt-4">
                <span className="text-3xl font-black text-ink">{tier.price}</span>
                {tier.period && <span className="text-sm text-ink-muted">{tier.period}</span>}
              </div>

              <ul className="mt-5 space-y-2">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-ink-muted">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand" viewBox="0 0 16 16" fill="none">
                      <path d="M4 8.5L7 11.5L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrent && isPaid ? (
                <button
                  onClick={handleManage}
                  disabled={portalLoading}
                  className="mt-6 block w-full rounded-lg py-2.5 text-center text-sm font-semibold bg-surface-100 text-ink hover:bg-surface-200 transition disabled:opacity-50"
                >
                  {portalLoading ? 'Loading...' : 'Manage subscription'}
                </button>
              ) : isCurrent ? (
                <div className="mt-6 block w-full rounded-lg py-2.5 text-center text-sm font-semibold bg-surface-100 text-ink-light">
                  Current plan
                </div>
              ) : isUpgrade ? (
                <button
                  onClick={() => handleUpgrade(tier.id as 'fortress' | 'citadel')}
                  disabled={loading === tier.id}
                  className={`mt-6 block w-full rounded-lg py-2.5 text-center text-sm font-semibold transition disabled:opacity-50 ${
                    highlight
                      ? 'bg-brand text-white hover:bg-brand-dark'
                      : 'bg-surface-100 text-ink hover:bg-surface-200'
                  }`}
                >
                  {loading === tier.id ? 'Redirecting...' : 'Upgrade'}
                </button>
              ) : isDowngrade && isPaid ? (
                <button
                  onClick={handleManage}
                  disabled={portalLoading}
                  className="mt-6 block w-full rounded-lg py-2.5 text-center text-sm font-semibold bg-surface-100 text-ink-muted hover:bg-surface-200 transition disabled:opacity-50"
                >
                  {portalLoading ? 'Loading...' : 'Downgrade'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Subscription info */}
      {subInfo && (
        <div className={`mt-4 rounded-xl border p-4 text-sm ${
          subInfo.cancel_at_period_end || subInfo.scheduled_change === 'cancelling'
            ? 'border-amber-200 bg-amber-50'
            : subInfo.scheduled_change
              ? 'border-blue-200 bg-blue-50'
              : 'border-surface-200 bg-white'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 shrink-0 ${
              subInfo.cancel_at_period_end ? 'text-amber-500' : subInfo.scheduled_change ? 'text-blue-500' : 'text-ink-muted'
            }`}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              {subInfo.cancel_at_period_end || subInfo.scheduled_change === 'cancelling' ? (
                <p className="text-amber-800">
                  Your subscription will be cancelled on{' '}
                  <strong>{new Date(subInfo.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                  You keep full access until then.
                </p>
              ) : subInfo.scheduled_change ? (
                <p className="text-blue-800">
                  Switching to <strong className="capitalize">{subInfo.scheduled_change}</strong> on{' '}
                  <strong>{new Date(subInfo.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                  You keep your current plan until then.
                </p>
              ) : (
                <p className="text-ink-muted">
                  Next billing date:{' '}
                  <strong>{new Date(subInfo.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Referral Section */}
      <div className="mt-8 rounded-xl border border-surface-200 bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
              <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-ink">Invite friends, get rewarded</h3>
            <p className="mt-1 text-sm text-ink-muted">
              Share your link — when someone subscribes, you both get 1 month free.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                readOnly
                value={referralLink}
                className="flex-1 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-xs text-ink-muted font-mono truncate"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={handleCopyReferral}
                className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white hover:bg-brand-dark transition"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
